import { createFileRoute, Link } from "@tanstack/react-router";
import { AppHeader } from "@/components/AppHeader";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — RoleBoard" },
      {
        name: "description",
        content:
          "How RoleBoard handles your audition data, uploaded tapes, AI insights, and your rights to export or delete your account.",
      },
      { property: "og:title", content: "Privacy Policy — RoleBoard" },
      {
        property: "og:description",
        content:
          "Your career data belongs to you. Read how RoleBoard collects, stores, and protects it.",
      },
      { property: "og:url", content: "https://roleboard.app/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://roleboard.app/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <AppHeader />
      <main className="flex-1">
        <article className="container max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16 prose prose-neutral dark:prose-invert">
          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Privacy Policy
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Last updated: May 2026
            </p>
          </header>

          <Section title="Our approach">
            <p>
              RoleBoard is built for actors. Your career data — auditions, self
              tapes, calendar entries, notes — belongs to you. We collect only
              what we need to run the product, and we never sell your data.
            </p>
          </Section>

          <Section title="What we collect">
            <ul>
              <li>
                <strong>Account info:</strong> email, name (optional), and
                authentication credentials managed by our auth provider.
              </li>
              <li>
                <strong>Audition data:</strong> project names, roles, casting
                directors, dates, formats, and any notes you choose to save.
              </li>
              <li>
                <strong>Uploaded files:</strong> self tapes and slates you
                upload to organize and prepare submissions.
              </li>
              <li>
                <strong>Calendar data:</strong> sync tokens and event metadata
                related to your auditions.
              </li>
              <li>
                <strong>Usage analytics:</strong> minimal product analytics
                (page views, error logs) to improve reliability.
              </li>
            </ul>
          </Section>

          <Section title="How uploaded files are used">
            <p>
              Uploaded tapes are stored privately and accessed only through
              authenticated, time-limited signed URLs. They are visible only to
              you. <strong>We do not use your uploaded content to train any
              public AI model.</strong>
            </p>
          </Section>

          <Section title="AI-generated insights">
            <p>
              RoleBoard offers optional AI-assisted features (e.g. slate
              transcription, audition pattern overviews). These are{" "}
              <strong>supportive informational tools only</strong>. They are not
              predictive casting systems and they do not score or evaluate you
              as a performer.
            </p>
            <p>
              When you use an AI feature, the minimum necessary content is sent
              to a trusted model provider for processing. Providers do not
              retain this content for training.
            </p>
          </Section>

          <Section title="Third-party services">
            <ul>
              <li>
                <strong>Supabase</strong> — database, authentication, and file
                storage.
              </li>
              <li>
                <strong>OpenAI / model providers</strong> — only when you
                trigger an AI feature.
              </li>
              <li>
                <strong>Hosting / CDN</strong> — to deliver the product.
              </li>
            </ul>
          </Section>

          <Section title="Your rights">
            <ul>
              <li>
                <strong>Export:</strong> download all your data as JSON from{" "}
                <Link to="/settings" className="underline">Settings</Link>.
              </li>
              <li>
                <strong>Deletion:</strong> permanently delete your account,
                files, and associated records at any time from Settings.
              </li>
            </ul>
          </Section>

          <Section title="Cookies & analytics">
            <p>
              We use a small number of essential cookies (for sign-in and
              preferences) and minimal product analytics. You can clear cookies
              from your browser at any time. We do not run advertising trackers.
            </p>
          </Section>

          <Section title="International data">
            <p>
              Your data may be stored and processed in regions where our
              infrastructure providers operate. We rely on those providers'
              standard contractual safeguards for international transfers.
            </p>
          </Section>

          <Section title="Security">
            <p>
              All traffic is encrypted in transit (HTTPS). Database access is
              scoped per user via Row Level Security. Uploaded files are private
              by default and never publicly listed.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Questions about this policy? Email{" "}
              <a href="mailto:hello@roleboard.app" className="underline">
                hello@roleboard.app
              </a>
              .
            </p>
          </Section>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      <div className="mt-3 text-[15px] leading-relaxed text-muted-foreground space-y-3">
        {children}
      </div>
    </section>
  );
}
